/**
 * ContactSubHeader — sub-header content for an ahoy_contact slideout.
 *
 * Layout: `Job title | Company badge`. Registered on
 * `ENTITY_TYPE_REGISTRY.ahoy_contact.components.subHeader`; consumers should
 * never import this directly — go through `SubHeaderSection`.
 */

import { useMemo } from 'react';

import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import { EntityBadgeById } from '../../components/badges/EntityBadgeById';
import { TextValueLabel } from '../../components/properties/labels/TextValueLabel';
import { useAssociations } from '../../hooks/useAssociations';
import { SubHeaderLayout } from './sub-header-layout';

export function ContactSubHeader({ object }: { object: CrmObject }) {
  // Pre-filter to the company association only — useAssociations triggers
  // a hydration fetch for every target it's given (see ensureEntity loop
  // in useAssociations.ts), so passing the full association list would
  // fan out fetches for emails / meetings / notes / etc. that this
  // sub-header never renders.
  const companyAssociations = useMemo(
    () =>
      (object.associations ?? []).filter(
        a => a.association_type_name === 'ahoy_contact_to_company'
      ),
    [object.associations]
  );

  const { associations: resolvedAssociations } = useAssociations(
    object.org_id,
    companyAssociations
  );

  const jobTitle = getPropertyValue<string>(object.properties?.ahoy_job_title);

  const companyAssoc = resolvedAssociations.find(
    a => a.association_type_name === 'ahoy_contact_to_company'
  );

  return (
    <SubHeaderLayout
      primary={
        jobTitle ? (
          // Canonical text label so entity-mention tags in the job title
          // render as badges instead of leaking raw markup (#12153). Font
          // classes mirror SubHeaderLayout's `text-xs md:text-sm`.
          <TextValueLabel
            value={jobTitle}
            orgId={object.org_id}
            fontSize='xs'
            className='md:text-sm'
          />
        ) : undefined
      }
      secondary={
        companyAssoc?.target_entity_id ? (
          <EntityBadgeById
            entityId={companyAssoc.target_entity_id}
            orgId={object.org_id}
            variant='xs'
          />
        ) : undefined
      }
    />
  );
}
